/** Dev mode → Overview (devmode-v1.html §1). */
import { getDevOverview, type BugStatus } from '../kiaanApi';
import { DevState, useDevData } from './useDevData';

export const BUG_PILL: Record<BugStatus, { label: string; cls: string }> = {
  new: { label: 'new', cls: 'kw-bad' },
  ai_fixing: { label: 'AI fixing', cls: 'kw-wip' },
  waits_ok: { label: 'waits your OK', cls: 'kw-wt' },
  fixed_dev: { label: 'fixed on dev', cls: 'kw-ok' },
  closed: { label: 'closed', cls: 'kw-no' },
};

export function Overview({ onOpenBugs }: { onOpenBugs?: () => void }) {
  const { data, loading, error } = useDevData(getDevOverview);
  if (!data) return <DevState loading={loading} error={error} />;
  const openBugs = data.bugs.filter((b) => b.status !== 'closed');
  const working = data.workers.filter((w) => w.status === 'working').length;
  return (
    <div className="kw-grid">
      <div className="kw-box">
        <h4>AI workers (Kiaan Work) <span>{working} working</span></h4>
        {data.workers.length === 0 && <div className="kw-small">No workers yet.</div>}
        {data.workers.map((w) => (
          <div key={w.name} className="kw-row">
            {w.name} · {w.role}
            <span className={`kw-pill kw-ml ${w.status === 'working' ? 'kw-wip' : w.status === 'idle' ? 'kw-ok' : 'kw-no'}`}>
              {w.status === 'working' ? (w.task ?? 'working') : w.status}
            </span>
          </div>
        ))}
        {typeof data.usedPercent === 'number' && (
          <div className="kw-row" style={{ color: 'var(--ink-3)' }}>Used this month: {data.usedPercent}% of limit</div>
        )}
      </div>
      <div className="kw-box">
        <h4>Bugs from the team <button type="button" className="kw-small" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--ink-3)' }} onClick={onOpenBugs}>{openBugs.length} open</button></h4>
        {openBugs.length === 0 && <div className="kw-small">No open bugs.</div>}
        {openBugs.slice(0, 5).map((b) => (
          <div key={b.id} className="kw-row">
            &quot;{b.note}&quot;{b.reporterName ? ` · ${b.reporterName}` : ''}
            <span className={`kw-pill kw-ml ${BUG_PILL[b.status].cls}`}>{BUG_PILL[b.status].label}</span>
          </div>
        ))}
      </div>
      <div className="kw-box">
        <h4>Waiting for you <span>{data.waiting.length}</span></h4>
        {data.waiting.length === 0 && <div className="kw-small">Nothing waits for you.</div>}
        {data.waiting.map((w, i) => (
          <div key={i} className="kw-row">
            {w.title}{w.detail ? ` · ${w.detail}` : ''}
            <span className="kw-pill kw-wt kw-ml">{w.action}</span>
          </div>
        ))}
        <div className="kw-row" style={{ color: 'var(--ink-3)' }}>Live changes still go through Ardhanex.</div>
      </div>
      <div className="kw-box">
        <h4>How this screen works <span>{data.screenMap?.screen ?? ''}</span></h4>
        {!data.screenMap && <div className="kw-small">No map for this screen yet.</div>}
        {data.screenMap && (
          <>
            <div className="kw-row">{data.screenMap.flow.join(' → ')}</div>
            {data.screenMap.rules.map((r, i) => (
              <div key={`r${i}`} className="kw-row"><span className="kw-pill kw-wip">Rule</span> {r}</div>
            ))}
            {data.screenMap.autos.map((a, i) => (
              <div key={`a${i}`} className="kw-row"><span className="kw-pill kw-wip">Auto</span> {a}</div>
            ))}
          </>
        )}
        <div className="kw-row">
          <button type="button" className="kw-btn kw-light" disabled title="Coming soon">＋ New screen</button>
          <button type="button" className="kw-btn kw-light" disabled title="Coming soon">✎ Change this</button>
        </div>
      </div>
    </div>
  );
}
